import { useEffect, useState } from 'react';
import {
  Drawer,
  Box,
  Button,
  Typography,
  Chip,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import StickyNote2RoundedIcon from '@mui/icons-material/StickyNote2Rounded';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import { amber, deepPurple, lime, red, green, grey } from '@mui/material/colors';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import axios from '../../api/axios';
import { WithPermission } from '../../Auth/WithPermission';

const ViewPlanInfo = ({ plan, onClose, handleRestorePlan }) => {

  const GET_ORG = "/org/";
  const [orgSettings, setOrgSettings] = useState(null);
  const [openModal, setOpenModal] = useState(false);

  useEffect(() => {
    if (plan) {
      console.log("plan ", plan)
      setOpenModal(true);
    }
  }, [plan]);

  const formatDateTooltip = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    axios
      .post(GET_ORG, {
        token,
        orgSettings: "true"
      })
      .then((res) => {
        const settings = res.data?.message?.settings;
        console.log("SETTINGS " , settings)
        setOrgSettings(settings);
      })
      .catch((err) => {
        console.error("Error fetching org settings:", err);
      });
  }, []);

  const handleArchiving = () => {
    handleRestorePlan(plan.id);
    onClose();
  }

  return (
    <Drawer
      anchor="right"
      open={openModal}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 600, // adjust width as needed
          height: '100vh', // full viewport height
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Box sx={{ p: 3, flexGrow: 1, overflowY: 'auto' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6"
            sx={{ m: 0, lineHeight: 1.2 }}>Archived Plan Info</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Metadata (static text) */}
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
          Archived {formatDateTooltip(plan?.archivedOn)} by {plan?.archivingUser?.first + " " + plan?.archivingUser?.last} | File date {formatDateTooltip(plan?.lastModifiedCadFileDateTime)}
        </Typography>

        {/* File */}
        <Typography variant="body1" sx={{ marginBottom: '24px', color: "grey" }}>
          <Inventory2Icon color="white" sx={{ verticalAlign: "bottom" }}/> {plan?.fileName || '[FileName.dwg]'}
        </Typography>

        {/* Chips */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
          <Chip
            label={plan?.builderName || '[BuilderName]'}
            sx={{ color: "grey", backgroundColor: '#80DEEA', fontWeight: 500 }}
          />
          {plan?.project !== "No Project Assigned" && (
            <Chip
              label={plan.project}
              sx={{
                color: "grey",
                backgroundColor: amber[400],
                fontWeight: 500,
              }}
            />
          )}
          {plan?.planNumber && (
            <Chip
              label={plan.planNumber}
              sx={{
                color: "grey",
                backgroundColor: deepPurple[100],
                fontWeight: 500,
              }}
            />
          )}

          {plan?.elevation && (
            <Chip
              label={plan.elevation}
              sx={{
                color: "grey",
                backgroundColor: lime[300],
                fontWeight: 500,
              }}
            />
          )}
          {plan?.lot && (
            <Chip
              label={plan.lot}
              sx={{
                color: "grey",
                fontWeight: 500,
              }}
            />
          )}
          {plan?.layout && (
            <Chip
              label={plan.layout}
              sx={{
                color: "grey",
                fontWeight: 500,
              }}
            />
          )}
          {plan?.buildStages?.length > 0 && plan?.buildStages[0]?.buildStageName && (
            <Chip
              label={plan?.buildStages[0]?.buildStageName}
              sx={{
                color: "grey",
                fontWeight: 500,
              }}
            />
          )}
          {plan?.options?.map((option, index) => (
            <Chip
              key={index}
              label={option.planOptionName || option.name || option}
              sx={{
                color: "grey",
                fontWeight: 500,
              }}
            />
          ))}

        </Box>

        {/* Anchor Info */}
        <Box display="flex" alignItems="center" sx={{ mb: 2, color: "grey" }}>
          {plan?.nonstandardAnchor ? (
            <CenterFocusStrongIcon sx={{ color: red[200], mr: 1 }} />
          ) : (
            <CenterFocusStrongIcon sx={{ color: green[200], mr: 1 }} />
          )}

          <Typography variant="body2">
            <strong>Anchor Placement:</strong>{' '}
            {plan?.nonstandardAnchor
              ? plan?.anchorNote || 'No anchor note provided.'
              : orgSettings?.standard_anchor_placement || 'Standard placement not defined'}
          </Typography>
        </Box>

        {/* Plan Notes */}
        {plan?.planNote && (
          <Box display="flex" alignItems="center">
            <StickyNote2RoundedIcon sx={{ mr: 1, color: grey[500] }} />
            <Typography variant="body2" sx={{ color: "grey" }}>
              <strong>Plan Notes:</strong> {plan.planNote}
            </Typography>
          </Box>
        )}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-start', margin: '24px' }}>
        <Button
          onClick={onClose}
          variant='contained'
        >
          CLOSE
        </Button>

        {!plan?.projectArchived && 
          <WithPermission rolesRequired={['admin', 'superadmin', 'useredit']}>
            <Button
              onClick={handleArchiving}
              variant="outlined" 
              sx={{
                mx: 1,
              }}
            >
              RESTORE PLAN
            </Button>
          </WithPermission>
        }

      </Box>
    </Drawer>
  );
};

export default ViewPlanInfo;
